import type { ProgressSettings } from "../progress/schema";
import type { ProgressSnapshot } from "../progress/session";
import { Icon } from "./Icon";
import { Header, type NavigationProps } from "./SiteHeader";

type ToggleSetting = keyof Pick<
  ProgressSettings,
  | "music"
  | "sfx"
  | "vibration"
  | "reducedMotion"
  | "reducedParticles"
  | "skipReadyCountdown"
>;

interface SettingToggle {
  readonly key: ToggleSetting;
  readonly title: string;
  readonly copy: string;
}

const visualToggles: readonly SettingToggle[] = [
  {
    key: "reducedMotion",
    title: "减少运动",
    copy: "关闭镜头缓动与非必要动画，让画面更安定。",
  },
  {
    key: "reducedParticles",
    title: "减少粒子",
    copy: "减少装饰光点，保留危险与拾取提示。",
  },
  {
    key: "skipReadyCountdown",
    title: "缩短准备提示",
    copy: "完成首次教学后，将新一局的准备时间缩短至 1 秒。",
  },
];

const audioToggles: readonly SettingToggle[] = [
  {
    key: "music",
    title: "环境音乐",
    copy: "让轻柔的环境声陪伴你的航程。",
  },
  {
    key: "sfx",
    title: "游戏音效",
    copy: "听见拾取、加速、潮汐与淘汰的反馈。",
  },
  {
    key: "vibration",
    title: "设备震动",
    copy: "在支持的设备上感受轻触反馈。",
  },
];

export function SettingsPage({
  progress,
  onNavigate,
  onBack,
  onChange,
  onAccount,
  onHelp,
  onAppearance,
}: NavigationProps & {
  readonly progress: ProgressSnapshot;
  readonly onBack: () => void;
  readonly onChange: (settings: ProgressSettings) => void;
  readonly onAccount: () => void;
  readonly onHelp: () => void;
  readonly onAppearance: () => void;
}) {
  const settings = progress.progress.settings;
  const renderToggle = ({ key, title, copy }: SettingToggle) => (
    <div className="setting-row" key={key}>
      <div>
        <strong>{title}</strong>
        <small id={`setting-${key}-description`}>{copy}</small>
      </div>
      <button
        className="switch"
        role="switch"
        aria-checked={settings[key]}
        aria-label={title}
        aria-describedby={`setting-${key}-description`}
        onClick={() => onChange({ ...settings, [key]: !settings[key] })}
      />
    </div>
  );
  return (
    <>
      <Header onNavigate={onNavigate} progress={progress} active="settings" />
      <main className="panel-page">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Make it yours</p>
            <h1>设置</h1>
            <p className="page-description">找到舒服的配色、声音与节奏。</p>
          </div>
          <button className="ghost-button" onClick={onBack}>
            <Icon name="back" />
            返回
          </button>
        </div>
        <div className="settings-layout">
          <section
            className="settings-section"
            aria-labelledby="visual-settings"
          >
            <div className="section-heading">
              <Icon name="eye" />
              <h2 id="visual-settings">画面与体验</h2>
            </div>
            <div className="setting-list">
              <label className="setting-row">
                <div>
                  <strong>配色模式</strong>
                  <small>深邃暮色，或柔和的航图浅色。</small>
                </div>
                <select
                  aria-label="配色模式"
                  value={settings.theme}
                  onChange={(event) =>
                    onChange({
                      ...settings,
                      theme: event.target.value as ProgressSettings["theme"],
                    })
                  }
                >
                  <option value="dark">暮色</option>
                  <option value="light">航图浅色</option>
                </select>
              </label>
              <label className="setting-row">
                <div>
                  <strong>性能档位</strong>
                  <small>推荐自动；设备发热或卡顿时可选择低性能。</small>
                </div>
                <select
                  aria-label="性能档位"
                  value={settings.performance}
                  onChange={(event) =>
                    onChange({
                      ...settings,
                      performance: event.target
                        .value as ProgressSettings["performance"],
                    })
                  }
                >
                  <option value="auto">自动</option>
                  <option value="quality">精细</option>
                  <option value="low">低性能</option>
                </select>
              </label>
              {visualToggles.map(renderToggle)}
            </div>
          </section>
          <section
            className="settings-section"
            aria-labelledby="audio-settings"
          >
            <div className="section-heading">
              <Icon name="sound" />
              <h2 id="audio-settings">声音与触感</h2>
            </div>
            <div className="setting-list">{audioToggles.map(renderToggle)}</div>
          </section>
        </div>
        <div className="save-notice" role="status">
          <Icon name={progress.user ? "cloud" : "shield"} />
          <div>
            <strong>{progress.message}</strong>
            <p>
              {progress.user
                ? "账户进度与游客进度分别保存。退出账户后会回到这个浏览器的游客进度。"
                : "游客进度只保存在当前浏览器。清除浏览器数据可能丢失纪录与外观；换设备需要使用已授权账户的云存档。"}
            </p>
          </div>
        </div>
        <div className="hero-actions">
          <button className="secondary-button" onClick={onAppearance}>
            <Icon name="skins" />
            外观与解锁
          </button>
          <button className="secondary-button" onClick={onAccount}>
            <Icon name="cloud" />
            个人主页账户云存档
          </button>
          <button className="secondary-button" onClick={onHelp}>
            <Icon name="help" />
            帮助与隐私
          </button>
        </div>
      </main>
    </>
  );
}
